import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { CalendarDays, CheckCircle2, ArrowLeft } from "lucide-react";
import { DEFAULT_HERO_IMAGE } from "../../config/serviceImages";
import { isUserLoggedIn } from "../common/Header";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const STORAGE_KEY   = "cartItems";
const DAYS_AHEAD    = 3; // + today, via includeToday below — "up to 3 days" from today

const getAuthToken = () =>
  localStorage.getItem("authToken")    ||
  localStorage.getItem("accessToken")  ||
  localStorage.getItem("access_token") ||
  localStorage.getItem("token") || "";

const authHeaders = () => ({
  "Content-Type": "application/json",
  ...(getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}),
});

const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;

// ─────────────────────────────────────────────────────────────────────────────
// Duration — up to 4 hours in 30-minute steps. The original instant order's
// price already covers the first hour (BASE_INCLUDED_MIN); only time booked
// beyond that costs EXTRA_RATE_PER_30MIN extra (confirmed pricing rule).
// ─────────────────────────────────────────────────────────────────────────────
const BASE_INCLUDED_MIN    = 60;
const EXTRA_RATE_PER_30MIN = 45;
const DURATION_OPTIONS_MIN = [60, 90, 120, 150, 180, 210, 240]; // 1h – 4h

const formatDurationLabel = (min) => {
  const h = Math.floor(min / 60);
  const half = min % 60 === 30;
  return half ? `${h}.5 Hrs` : `${h} Hr${h > 1 ? "s" : ""}`;
};

const extraChargeFor = (min) => {
  const extraMin = Math.max(0, min - BASE_INCLUDED_MIN);
  return Math.ceil(extraMin / 30) * EXTRA_RATE_PER_30MIN;
};

// ─────────────────────────────────────────────────────────────────────────────
// Slot availability — duration=3 is the dedicated 30-min, 7am-7pm grid for
// this flow (see INSTANT_RESCHEDULE_SLOTS in labour_service/views.py),
// independent of Schedule Booking's own 1/2-hour category grids.
// ─────────────────────────────────────────────────────────────────────────────
const fetchSlotAvailability = async (serviceId) => {
  const res  = await fetch(`${API_BASE_URL}/api/booking/slot-availability`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ serviceId: String(serviceId), duration: 3, daysAhead: DAYS_AHEAD, includeToday: true }),
  });
  const data = await res.json();
  if (!res.ok || !data?.success) throw new Error(data?.message || "Unable to load slot availability.");
  return Array.isArray(data?.days) ? data.days : [];
};

const formatTodayDDMMYYYY = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
};

const parseSlotClockTime = (timeStr) => {
  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(String(timeStr || "").trim());
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const ap = m[3].toUpperCase();
  if (ap === "PM" && h !== 12) h += 12;
  if (ap === "AM" && h === 12) h = 0;
  return { h, min };
};

const slotTimeToday = (timeStr) => {
  const parsed = parseSlotClockTime(timeStr);
  if (!parsed) return null;
  const d = new Date();
  d.setHours(parsed.h, parsed.min, 0, 0);
  return d;
};

const isSlotTooSoon = (slot, date) => {
  if (date !== formatTodayDDMMYYYY()) return false;
  const dt = slotTimeToday(slot?.time);
  if (!dt) return false;
  return dt.getTime() <= Date.now() + 60 * 60 * 1000;
};

const isSlotSelectable = (slot, date) =>
  Boolean(slot?.availability) && Number(slot?.seats || 0) - Number(slot?.booked || 0) > 0 && !isSlotTooSoon(slot, date);

const getSlotState = (slot, date) => {
  const seats  = Number(slot?.seats || 0);
  const booked = Number(slot?.booked || 0);
  if (booked >= seats) return "booked";
  if (!slot?.availability) return "unavailable";
  if (isSlotTooSoon(slot, date)) return "too-soon";
  return "available";
};

const formatDateLabel = (date) => {
  if (date === formatTodayDDMMYYYY()) return "Today";
  const [dd, mm, yyyy] = String(date || "").split("-");
  if (!dd) return date;
  const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
  if (isNaN(d.getTime())) return date;
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
};

const resolveImage = (path) => {
  if (!path) return DEFAULT_HERO_IMAGE;
  return path.startsWith("http") ? path : `${API_BASE_URL}${path}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// INSTANT RESCHEDULE — reached from InstantOrderTracking.js's "Schedule This
// Service Instead" (only shown once the instant waterfall found nobody).
// Deliberately NOT the generic 3-category Schedule Booking wizard: the exact
// service is already known (no category/service picking needed), slots are a
// 30-min grid instead of fixed 1/2-hour blocks, and duration is flexible up
// to 4 hours with a per-30-min surcharge instead of flat per-category pricing.
// Lands as a plain dated cart item (no schedule_booking/instant flag) in
// scheduled_order_col via the normal checkout-schedule summary page, exactly
// like the per-service electrical booking pages (FanBookingPage.js etc.)
// already do for their own slot picking.
// ─────────────────────────────────────────────────────────────────────────────
const InstantReschedulePage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [order,   setOrder]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  const [days, setDays]                     = useState([]);
  const [loadingAvailability, setLoadingAv] = useState(false);
  const [availError, setAvailError]         = useState("");
  const [selectedDate, setSelectedDate]     = useState("");
  const [selectedTime, setSelectedTime]     = useState("");
  const [selectedSlotId, setSelectedSlotId] = useState("");
  const [durationMin, setDurationMin]       = useState(BASE_INCLUDED_MIN);
  const [confirming, setConfirming]         = useState(false);

  useEffect(() => {
    if (!orderId) return;
    (async () => {
      try {
        const res  = await fetch(`${API_BASE_URL}/api/user/instant-order/${orderId}/status/`, { headers: authHeaders() });
        const data = await res.json();
        if (res.ok && data?.success) setOrder(data.data);
        else setError(data?.message || "Order not found.");
      } catch (e) {
        setError("Unable to load this order.");
      } finally {
        setLoading(false);
      }
    })();
  }, [orderId]);

  const mainItem   = order?.items?.[0] || null;
  const serviceId  = mainItem?.service_id || order?.order_id || orderId;
  const basePrice  = Number(mainItem?.price || 0);

  useEffect(() => {
    if (!serviceId) return;
    (async () => {
      setLoadingAv(true);
      setAvailError("");
      try {
        const fetched = await fetchSlotAvailability(serviceId);
        setDays(fetched);
        const first = fetched.find((d) => d.slots.some((s) => isSlotSelectable(s, d.date)))?.date || fetched[0]?.date || "";
        setSelectedDate(first);
      } catch (e) {
        setAvailError(e?.message || "Failed to load available slots.");
      } finally {
        setLoadingAv(false);
      }
    })();
  }, [serviceId]);

  const activeDay = days.find((d) => d.date === selectedDate) || null;
  const extraCharge = extraChargeFor(durationMin);
  const totalPrice  = basePrice + extraCharge;

  const pickDate = (date) => {
    setSelectedDate(date);
    setSelectedTime(""); setSelectedSlotId("");
  };
  const pickTime = (time, slotId) => {
    setSelectedTime(time);
    setSelectedSlotId(slotId || "");
  };

  const handleConfirm = () => {
    if (!selectedDate || !selectedTime || !mainItem) return;
    setConfirming(true);
    try {
      const cartItem = {
        id:       `resch-${serviceId}-${selectedDate}-${selectedTime}`.replace(/\s+/g, ""),
        title:    order.service_name || order.service || mainItem.service,
        name:     order.service_name || order.service || mainItem.service,
        short:    `${formatDurationLabel(durationMin)} visit`,
        subtitle: `${formatDurationLabel(durationMin)} visit`,
        category: mainItem.category_label || (order.service_category || [])[0] || "Service",
        price:    totalPrice,
        image:    resolveImage(mainItem.image),
        qty:      1,
        bookingDate:   selectedDate,
        bookingTime:   selectedTime,
        bookingSlotId: selectedSlotId,
        // Informational only — not read by place_order(), just carried
        // through for the order record / support reference.
        reschedule_duration_minutes: durationMin,
        is_instant_reschedule:       true,
        original_instant_order_id:   order.order_id || orderId,
      };

      try {
        const existing  = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
        const withoutSame = Array.isArray(existing) ? existing.filter((i) => i.id !== cartItem.id) : [];
        localStorage.setItem(STORAGE_KEY, JSON.stringify([...withoutSame, cartItem]));
        window.dispatchEvent(new Event("cart-updated"));
      } catch { /* localStorage unavailable — navigation state below still works */ }

      if (isUserLoggedIn()) {
        navigate("/checkout-schedule/summary", { state: { cartItems: [cartItem] } });
      } else {
        navigate("/user/user-login", { state: { redirectTo: "/checkout-schedule/summary", cartItems: [cartItem] } });
      }
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return <div style={S.page}><div style={S.stateBox}>Loading…</div></div>;
  }
  if (error || !order || !mainItem) {
    return (
      <div style={S.page}>
        <div style={S.errorBox}>{error || "Order not found."}</div>
        <Link to="/" style={S.backLink}><ArrowLeft size={16} /> Back to Home</Link>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.layout}>
        <Link to={`/instant-tracking/${orderId}`} style={S.backLink}><ArrowLeft size={16} /> Back</Link>

        <div style={S.summaryBar}>
          <img src={resolveImage(mainItem.image)} alt={order.service_name} style={S.summaryImg}
               onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = DEFAULT_HERO_IMAGE; }} />
          <div style={S.summaryBody}>
            <div style={S.summaryTitle}>{order.service_name || order.service}</div>
            <div style={S.summarySub}>Same service, now booked for a confirmed time slot</div>
          </div>
          <div style={S.summaryPrice}>{fmt(basePrice)}</div>
        </div>

        <h1 style={S.title}>Choose Date, Time &amp; Duration</h1>
        <p style={S.subtitle}>Book up to {DAYS_AHEAD} days ahead — 30-minute slots, 7:00 AM to 7:00 PM.</p>

        <div style={S.legend}>
          <span style={S.legendItem}><span style={{ ...S.legendDot, background: "#22c55e" }} /> Available</span>
          <span style={S.legendItem}><span style={{ ...S.legendDot, background: "#ef4444" }} /> Booked</span>
          <span style={S.legendItem}><span style={{ ...S.legendDot, background: "#9ca3af" }} /> Unavailable</span>
        </div>

        <div style={S.columns}>
          {/* Column 1 — Date */}
          <div style={S.col}>
            <div style={S.colTitle}>1. Select Date</div>
            {loadingAvailability ? (
              <div style={S.stateBox}>Loading…</div>
            ) : availError ? (
              <div style={S.errorBox}>{availError}</div>
            ) : (
              <div style={S.dateList}>
                {days.map((day) => {
                  const hasOpen  = day.slots.some((s) => isSlotSelectable(s, day.date));
                  const selected = selectedDate === day.date;
                  return (
                    <button
                      key={day.date} type="button"
                      disabled={!hasOpen}
                      style={{ ...S.dateRow, ...(selected ? S.dateRowActive : {}), ...(!hasOpen ? S.dateRowDisabled : {}) }}
                      onClick={() => hasOpen && pickDate(day.date)}
                    >
                      <CalendarDays size={16} color={selected ? "#fff" : hasOpen ? "#16a34a" : "#9ca3af"} />
                      <span style={S.dateRowLabel}>{formatDateLabel(day.date)}</span>
                      <span style={{ ...S.dateRowSub, ...(selected ? { color: "#bbf7d0" } : {}) }}>
                        {hasOpen ? `${day.slots.filter((s) => isSlotSelectable(s, day.date)).length} open` : "Full"}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Column 2 — Time slot */}
          <div style={S.col}>
            <div style={S.colTitle}>2. Select Time Slot</div>
            <div style={S.slotGrid}>
              {(activeDay?.slots || []).map((slot) => {
                const state      = getSlotState(slot, activeDay?.date);
                const selectable = state === "available";
                const freeSeats  = Number(slot.seats || 0) - Number(slot.booked || 0);
                const selected   = selectedTime === slot.time;
                return (
                  <button
                    key={`${activeDay?.date}-${slot.time}`} type="button"
                    disabled={!selectable}
                    style={{
                      ...S.slotBtn,
                      ...(selected ? S.slotBtnActive : {}),
                      ...(state === "booked" ? S.slotBtnBooked : {}),
                      ...(state === "unavailable" || state === "too-soon" ? S.slotBtnDisabled : {}),
                    }}
                    onClick={() => selectable && pickTime(slot.time, slot._id)}
                  >
                    <div style={S.slotTimeRow}>
                      <span style={{ ...S.slotTime, ...(selected ? { color: "#fff" } : {}) }}>{slot.time}</span>
                      {selected && <CheckCircle2 size={12} color="#fff" />}
                    </div>
                    <div style={{ ...S.slotMeta, ...(selected ? { color: "#bbf7d0" } : {}), ...(state === "booked" ? { color: "#dc2626", fontWeight: 700 } : {}) }}>
                      {state === "available" ? `${freeSeats} left` : state === "booked" ? "Booked" : state === "too-soon" ? "Too soon" : "Unavailable"}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Column 3 — Duration */}
          <div style={S.col}>
            <div style={S.colTitle}>3. Select Duration (up to 4 Hrs)</div>
            <div style={S.durationList}>
              {DURATION_OPTIONS_MIN.map((min) => {
                const selected = durationMin === min;
                const extra = extraChargeFor(min);
                return (
                  <button
                    key={min} type="button"
                    style={{ ...S.durationRow, ...(selected ? S.durationRowActive : {}) }}
                    onClick={() => setDurationMin(min)}
                  >
                    <span style={S.durationLabel}>{formatDurationLabel(min)}</span>
                    <span style={S.durationExtra}>{extra > 0 ? `+${fmt(extra)}` : "Included"}</span>
                  </button>
                );
              })}
            </div>
            <div style={S.scheduleNote}>
              First hour is included in the service price shown above; every extra 30 minutes adds {fmt(EXTRA_RATE_PER_30MIN)}.
            </div>
          </div>
        </div>

        <div style={S.stickyBar}>
          <div style={S.stickyLeft}>
            <div style={S.stickyLabel}>Total for {formatDurationLabel(durationMin)} visit</div>
            <div style={S.stickyPrice}>{fmt(totalPrice)}</div>
          </div>
          <button
            type="button"
            style={{ ...S.continueBtn, ...(!selectedTime ? S.continueBtnDisabled : {}) }}
            disabled={!selectedTime || confirming}
            onClick={handleConfirm}
          >
            {confirming ? "Please wait…" : "Confirm & Continue"}
          </button>
        </div>
      </div>
    </div>
  );
};

const S = {
  page:   { background: "#f8fafc", minHeight: "100vh", padding: "20px", fontFamily: "Arial, sans-serif", color: "#111827" },
  layout: { maxWidth: "1100px", margin: "0 auto" },
  backLink: { display: "inline-flex", alignItems: "center", gap: "6px", color: "#6b7280", textDecoration: "none", fontSize: "13px", fontWeight: 600, marginBottom: "16px" },

  stateBox: { textAlign: "center", padding: "24px", color: "#6b7280", fontSize: "13px" },
  errorBox: { textAlign: "center", padding: "24px", color: "#dc2626", fontSize: "13px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "12px" },

  summaryBar: { display: "grid", gridTemplateColumns: "auto minmax(0, 1fr) auto", alignItems: "center", gap: "16px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: "16px", padding: "16px 20px", marginBottom: "20px" },
  summaryImg: { width: "64px", height: "64px", borderRadius: "12px", objectFit: "cover", display: "block" },
  summaryBody: { minWidth: 0 },
  summaryTitle: { fontSize: "16px", fontWeight: 800 },
  summarySub: { fontSize: "12.5px", color: "#6b7280", marginTop: "4px" },
  summaryPrice: { fontSize: "18px", fontWeight: 800, color: "#16a34a" },

  title: { margin: "0 0 4px", fontSize: "22px", fontWeight: 800 },
  subtitle: { margin: "0 0 16px", fontSize: "13.5px", color: "#6b7280" },

  legend: { display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "16px" },
  legendItem: { display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12px", fontWeight: 600, color: "#6b7280" },
  legendDot: { width: "9px", height: "9px", borderRadius: "50%", display: "inline-block" },

  columns: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "20px", marginBottom: "90px", alignItems: "start" },
  col: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "16px", padding: "18px" },
  colTitle: { fontSize: "13px", fontWeight: 800, marginBottom: "14px" },
  scheduleNote: { background: "#f0fdf4", borderRadius: "10px", padding: "10px 12px", marginTop: "14px", fontSize: "12px", color: "#15803d", lineHeight: 1.4 },

  dateList: { display: "flex", flexDirection: "column", gap: "8px" },
  dateRow: { display: "flex", alignItems: "center", gap: "10px", padding: "12px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", textAlign: "left" },
  dateRowActive: { background: "#16a34a", borderColor: "#16a34a", color: "#fff" },
  dateRowDisabled: { opacity: 0.45, cursor: "not-allowed" },
  dateRowLabel: { flex: 1, fontSize: "13px", fontWeight: 700, color: "inherit" },
  dateRowSub: { fontSize: "11.5px", fontWeight: 700, color: "#16a34a" },

  slotGrid: { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "8px", maxHeight: "420px", overflowY: "auto", paddingRight: "2px" },
  slotBtn:  { padding: "9px 6px", borderRadius: "10px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", textAlign: "center" },
  slotBtnActive: { border: "1.5px solid #16a34a", background: "#16a34a" },
  slotBtnBooked: { border: "1.5px solid #fecaca", background: "#fef2f2", cursor: "not-allowed" },
  slotBtnDisabled: { opacity: 0.5, cursor: "not-allowed", background: "#f3f4f6" },
  slotTimeRow: { display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" },
  slotTime: { fontSize: "12px", fontWeight: 800, color: "#111827" },
  slotMeta: { fontSize: "10px", color: "#9ca3af", marginTop: "3px" },

  durationList: { display: "flex", flexDirection: "column", gap: "8px" },
  durationRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "11px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer" },
  durationRowActive: { border: "1.5px solid #16a34a", background: "#f0fdf4" },
  durationLabel: { fontSize: "13px", fontWeight: 700 },
  durationExtra: { fontSize: "12px", fontWeight: 700, color: "#16a34a" },

  stickyBar: { position: "fixed", left: 0, right: 0, bottom: 0, background: "#fff", borderTop: "1px solid #e5e7eb", padding: "14px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: "0 -4px 16px rgba(0,0,0,0.06)", zIndex: 20 },
  stickyLeft: {},
  stickyLabel: { fontSize: "12px", color: "#6b7280" },
  stickyPrice: { fontSize: "20px", fontWeight: 800 },
  continueBtn: { border: "none", background: "#16a34a", color: "#fff", fontWeight: 700, fontSize: "14px", padding: "13px 28px", borderRadius: "12px", cursor: "pointer" },
  continueBtnDisabled: { background: "#9ca3af", cursor: "not-allowed" },
};

export default InstantReschedulePage;
